import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  ButtonCloseDirective,
  ButtonDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
} from '@coreui/angular-pro';
import { ServerAuthUrlPipe } from 'auth';
import { CalendarEvent } from 'calendar';
import { TranslateDirective, TranslatePipe } from 'i18n';
import { Subject } from 'rxjs';
import { contrastColor, CountdownComponent, Logger, SafeHtmlPipe, SafeUrlPipe, setAlpha, SlideEvent, SlidePanelComponent, SlidePanelModule, SlideState, User } from 'shared';
import { Invitation, } from '../../models/invitation';

import moment from 'moment';
import { MediaCardComponent } from 'media';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type InvitationDialogAction =
  | { type: 'accept' }
  | { type: 'decline' }
  | { type: 'join' }
  | { type: 'close' };

type EventState = 'ended' | 'live' | 'imminent' | 'upcoming' | undefined;

@Component({
  selector: 'app-invitation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonDirective,
    ModalBodyComponent,
    ModalComponent,
    ModalFooterComponent,
    // ModalHeaderComponent,
    // ModalTitleDirective,
    // SafeUrlPipe,
    TranslatePipe,
    TranslateDirective,
    // ServerAuthUrlPipe,
    SafeHtmlPipe,
    SlidePanelModule,
    TranslateDirective,
    TranslatePipe,
    CountdownComponent,
    MediaCardComponent
  ],
  templateUrl: './invitation-dialog.component.html',
  styleUrls: ['./invitation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitationDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  
  @Input({ required: true }) invitation!: Invitation;
  @Input() invitationToken: string = '';
  @Input() user: User | null = null;

  @ViewChild('slidePanel') slidePanel!: SlidePanelComponent;
  @ViewChildren('panel', { read: ElementRef }) panels!: QueryList<ElementRef<HTMLElement>>;
  
  visible = signal(true);
  event = computed<CalendarEvent | null>(() => this.invitation.data ?? null);

  contentHeight = signal(0);
  slidePanelId = signal<string>('main');
  private resizeObserver!: ResizeObserver;
  private actionsSubject = new Subject<InvitationDialogAction>();
  actions$ = this.actionsSubject.asObservable();

  private now = signal(Date.now());
  eventState = computed<EventState>(() => {
    const event = this.event();
    if (event) {
      const now = this.now();
      if (now >= event.timeTo) return 'ended';
      if (now >= event.timeFrom) return 'live';
      const startsInMinutes = (event.timeFrom - now) / 60_000;
      return startsInMinutes <=  this.joinMinutes ? 'imminent' : 'upcoming';
    }
    return undefined;
  });

  canJoin = computed(() => {
    const state = this.eventState();
    return state == 'live' || state == 'imminent' || state == 'upcoming';
  });

  propertyValue(name: string, defaultValueProvider: () => string) {
    return getComputedStyle(document.documentElement).getPropertyValue(name) ?? defaultValueProvider();
  }
  
  defaultColor = () => this.propertyValue('--cui-primary', () => '#017bd9');
  countdownColor = this.propertyValue('--cui-warning-border-subtle', this.defaultColor);
  ribbonColor = computed(() => {
      const color = this.event()?.color || this.defaultColor();
      return contrastColor(color!);
  });
  ribbonBackgroundColor = computed(() => {
    const color = this.event()?.color || this.defaultColor();
    return setAlpha(color!, 0.75);
  });

  moment = moment;
  joinMinutes = 15;

  protected logger = new Logger('InvitationDialogComponent');
  
  constructor(private destroyRef: DestroyRef) {
    setInterval(() => this.now.set(Date.now()), 1000);
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    const trigger = () => {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeight();
      });
      this.panels.forEach(panel =>
        this.resizeObserver.observe(panel.nativeElement)
      );
    }
    
    this.panels
        .changes
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          trigger();
        });
    trigger();
  }

  ngOnDestroy() {
    this.complete();
  }

  accept() {
    this.actionsSubject.next({ type: 'accept' });
    this.slidePanelId.set('join');
  }

  decline() {
    this.actionsSubject.next({ type: 'decline' });
    this.complete();
  }

  join() {
    this.actionsSubject.next({ type: 'join' });
    this.complete();
  }

  close() {
    this.actionsSubject.next({ type: 'close' });
    this.complete();
  }

  complete() {
    this.visible.set(false);
    this.actionsSubject.complete();
  }

  onVisibleChange(visible: boolean) {
    this.visible.set(visible);
    if (!visible) {
      this.close();
    }
  }

  onSlide(event: SlideEvent) {
    // this.logger.debug('onSlide', event);
    if (event.state == SlideState.START) {
      this.updateHeight();
    }
  }
  
  onCountdownRemaining(seconds: number) {
  }

  private updateHeight = () => {
    const panelsArray = this.panels.toArray();
    if (!panelsArray.length) return;
    const index = this.slidePanelId() === 'main' ? 0 : 1;
    const el = panelsArray[index]?.nativeElement;
    if (!el) return;
    this.contentHeight.set(el.offsetHeight);
  }
}
